export const SENTRY_RUNTIME_CONFIG = {
  tracesSampleRate: 0.1,
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 1,
} as const;

export interface SentryLike {
  captureException: (
    error: Error,
    context: {
      tags: Record<string, string>;
      extra: Record<string, unknown>;
    },
  ) => void;
}

export interface SentryContext {
  tenantId?: string;
  route?: string;
  extra?: Record<string, unknown>;
}

export function buildSentryTags(context: SentryContext): Record<string, string> {
  return Object.fromEntries(
    Object.entries({
      tenantId: context.tenantId,
      route: context.route,
    }).filter((entry): entry is [string, string] => typeof entry[1] === "string" && entry[1].length > 0),
  );
}

export function captureAppError(
  error: Error,
  context: SentryContext,
  sentry = getGlobalSentry(),
): void {
  if (!sentry) {
    if (typeof console !== "undefined") console.error("Application error", { error, context });
    return;
  }

  sentry.captureException(error, {
    tags: buildSentryTags(context),
    extra: context.extra ?? {},
  });
}

export function buildConvexSentryContext({
  tenantId,
  functionName,
}: {
  tenantId: string;
  functionName: string;
}): SentryContext {
  return {
    tenantId,
    route: `convex:${functionName}`,
  };
}

export function getGlobalSentry(): SentryLike | undefined {
  const maybeGlobal = globalThis as typeof globalThis & { Sentry?: SentryLike };
  return maybeGlobal.Sentry;
}
